import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import DashboardService from "modules/dashboard/api/DashboardService";
import { GenericDataState } from "type";
import { PagedData, SORT_DIRECTION } from "types";
import InspectionsService from "modules/dashboard/api/InspectionsService";
import { DashboardInspectionFilter, DashboardInspectionFilterResponse, ProjectInspectionListItem } from "types/dashboard";
import AnalyticService from "modules/dashboard/api/AnalyticService";
import { DataState } from ".";

interface DashboardInspectionState {
  params: DashboardInspectionFilter & { sortBy?: any; };
  dataState: DataState;
  data: PagedData<ProjectInspectionListItem>;
  filters: GenericDataState<DashboardInspectionFilterResponse>;
}

type DashboardDataState = {
  summary: GenericDataState<any>;
  charts: GenericDataState<any>;
  inspections: DashboardInspectionState;
  analytics: GenericDataState<any>;
};

const initialState: DashboardDataState = {
  summary: {
    dataState: DataState.INITIAL,
    data: undefined as unknown as any,
  },
  charts: { dataState: DataState.INITIAL, data: [] },
  inspections: {
    params: {
      pageSize: 10,
      sortDirection: SORT_DIRECTION.Ascending,
      sortFieldName: "name",
      page: 1,

    },
    dataState: DataState.INITIAL,
    data: {
      items: [],
      totalCount: 0,
      totalPages: 0,
    },
    filters: {
      dataState: DataState.INITIAL,
      data: undefined as unknown as DashboardInspectionFilterResponse,
    },
  },
  analytics: {
    dataState: DataState.INITIAL,
    data: undefined as unknown as any,
  },
};

export const getDashboardAnalyticsProjects = createAsyncThunk(
  "dashboard/analytics/projects",
  async (params, thunkApi) => {
    try {
      const result = await AnalyticService.getProjects(params);

      return result?.data?.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getSummary = createAsyncThunk(
  "dashboard/getSummary",
  async (
    params: {
    },
    thunkApi,
  ) => {
    try {
      const result = await DashboardService.getDashboardSummary();

      return result?.data?.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getCharts = createAsyncThunk(
  "dashboard/getCharts",
  async (
    params,
    thunkApi,
  ) => {
    try {
      const result = await DashboardService.getDashboardCharts();

      return result?.data?.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getInspectionsList = createAsyncThunk(
  "dashboard/getInspectionsList",
  async (
    params: {
      filters: DashboardInspectionFilter & { searchFilter: string; sortBy: any; };
    },
    thunkApi,
  ) => {
    try {
      console.log("getInspectionsList thunk called with params:");
      const { dashboard } = thunkApi.getState() as { dashboard: DashboardDataState };
      const result = await InspectionsService.getDashboardInspections({ ...dashboard.inspections.params, ...params.filters });

      return result?.data?.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getInspectionsFilters = createAsyncThunk(
  "dashboard/getInspectionsFilters",
  async (
    params: {
      filters: DashboardInspectionFilter;
    },
    thunkApi,
  ) => {
    try {
      const result = await InspectionsService.getFilters(params.filters ?? initialState.inspections.params);

      return result?.data?.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    resetDashboardState: (state) => {
      state.summary.dataState = DataState.INITIAL;
      state.summary.data = [];
      state.charts.dataState = DataState.INITIAL;
      state.charts.data = [];
      state.inspections.dataState = DataState.INITIAL;
      state.inspections.data = {
        items: [],
        totalCount: 0,
        totalPages: 0,
      };
      state.inspections.params = {
        pageSize: 10,
        sortDirection: SORT_DIRECTION.Ascending,
        sortFieldName: "name",
        page: 1,
      };
      state.inspections.filters.dataState = DataState.INITIAL;
      state.inspections.filters.data = undefined as unknown as DashboardInspectionFilterResponse;
    },
    inspectionsFilterSet: (state, action) => {
      if (!state.inspections) {
        return;
      }
      if (!state?.inspections?.params) {
        state.inspections.params = {
          pageSize: 10,
          sortDirection: SORT_DIRECTION.Ascending,
          sortFieldName: "name",
          page: 1,
          ...action.payload,
        };
      }
      state.inspections.params = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSummary.pending, (state) => {
      state.summary.dataState = DataState.LOADING;
    })
      .addCase(getSummary.fulfilled, (state, action) => {
        state.summary.dataState = DataState.FULFILLED;
        state.summary.data = action.payload;
      })
      .addCase(getSummary.rejected, (state) => {
        state.summary.dataState = DataState.ERROR;
      })
      .addCase(getCharts.pending, (state) => {
        state.charts.dataState = DataState.LOADING;
      })
      .addCase(getCharts.fulfilled, (state, action) => {
        state.charts.dataState = DataState.FULFILLED;
        state.charts.data = action.payload;
      })
      .addCase(getCharts.rejected, (state) => {
        state.charts.dataState = DataState.ERROR;
      })
      .addCase(getInspectionsList.pending, (state) => {
        console.log("getInspectionsList pending");
        state.inspections.dataState = DataState.LOADING;
      })
      .addCase(getInspectionsList.fulfilled, (state, action) => {
        state.inspections.dataState = DataState.FULFILLED;
        state.inspections.data = action.payload;
      })
      .addCase(getInspectionsList.rejected, (state) => {
        state.inspections.dataState = DataState.ERROR;
      })
      .addCase(getInspectionsFilters.pending, (state) => {
        state.inspections.filters.dataState = DataState.LOADING;
      })
      .addCase(getInspectionsFilters.fulfilled, (state, action) => {
        state.inspections.dataState = DataState.FULFILLED;
        state.inspections.filters.data = action.payload;
      })
      .addCase(getDashboardAnalyticsProjects.pending, (state) => {
        state.analytics.dataState = DataState.LOADING;
      })
      .addCase(getDashboardAnalyticsProjects.fulfilled, (state, action) => {
        state.analytics.dataState = DataState.FULFILLED;
        state.analytics.data = action.payload;
      })
      .addCase(getDashboardAnalyticsProjects.rejected, (state) => {
        state.analytics.dataState = DataState.ERROR;
      });
  },
});

export default dashboardSlice.reducer;

export const selectDashboardSummary = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data;
export const selectDashboardProjects = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.projects;
export const selectDashboardProjectsCount = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.projectsCount;
export const selectDashboardAssignmentsCount = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.assignmentsCount;
export const selectDashboardBudgetsCount = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.budgetsCount;
export const selectDashboardReportsCount = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.reportsCount;
export const selectDashboardInspectionsCount = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.data?.inspectionsCount;
export const selectDashboardSummaryState = (state: { dashboard: DashboardDataState }) => state.dashboard.summary.dataState;
export const selectDashboarCharts = (state: { dashboard: DashboardDataState }) => state.dashboard.charts.data;
export const selectDashboarChartsState = (state: { dashboard: DashboardDataState }) => state.dashboard.charts.dataState;
export const selectDashboardInspections = (state: { dashboard: DashboardDataState }) => state.dashboard.inspections.data;
export const selectDashboardInspectionsState = (state: { dashboard: DashboardDataState }) => state.dashboard.inspections.dataState;
export const selectDashboardInspectionFilters = (state: { dashboard: DashboardDataState }) => state.dashboard.inspections.filters.data;
export const selectDashboardInspectionFiltersState = (state: { dashboard: DashboardDataState }) => state.dashboard.inspections.filters.dataState;
export const selectDashboardInspectionParams = (state: { dashboard: DashboardDataState }) => state.dashboard.inspections.params;
export const selectDashboardAnalyticsProjects = (state: { dashboard: DashboardDataState }) => state.dashboard.analytics.data;

export const {
  resetDashboardState,
  inspectionsFilterSet,
} = dashboardSlice.actions;
